/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/trackingDecision.scss';

import React, {MouseEventHandler, useContext, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {CloseButton, Link, Loader, voidFunction, WithBackground} from '@dvrd/dvr-controls';

import {AppContext} from '../misc/appContext';

import {BluemateButton} from '../controls';

interface Props {
    onDecide: (decision: boolean) => MouseEventHandler;
    onClose: MouseEventHandler;
    active: boolean;
    loading: boolean;
}

export default function TrackingDecision(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {onDecide, loading, active, onClose} = props;
    const [infoActive, setInfoActive] = useState(false);

    function onOpenInfo() {
        setInfoActive(true);
    }

    function onCloseInfo() {
        setInfoActive(false);
    }

    function renderInfo() {
        return (
            <WithBackground active={infoActive} onClose={onCloseInfo}>
                <div className='tracking-decision-info-popup default-popup'>
                    <div className='body-container'>
                        <p>
                            {t(
                                'tracking.info.paragraph1',
                                'In april 2022 heeft Bol een update in zijn e-mailsysteem doorgevoerd waardoor externe links (alle links die niet direct naar Bol leiden) als tekst worden toegevoegd in de mail. Bol geeft aan dit uit veiligheidsoverwegingen te hebben gedaan, omdat klanten hierdoor kunnen zien waar ze heen gestuurd worden voordat ze op een link / knop klikken.'
                            )}
                            <br />
                            <br />
                            {t(
                                'tracking.info.paragraph2',
                                'Door deze update werd de layout van de e-mails die via ons platform werden verzonden mogelijk verstoord, hierdoor hebben wij onze statistieken van de e-mails tijdelijk uitgeschakeld.'
                            )}
                            <br />
                            <br />
                            {t(
                                'tracking.info.paragraph3',
                                'Inmiddels hebben we in gesprek met Bol een oplossing gevonden waarmee wij weer statistieken van de e-mails kunnen tonen. Met deze oplossing worden de e-mails verzonden door een "noreply" adres, waardoor het platform van Bol de mail anders verwerkt en niet de links als tekst in de mails plaatst. De klant zal de mail ontvangen van het adres noreply@verkopen.bol.com Het gevolg hiervan is dat de klant niet meer zal kunnen reageren op de e-mail, omdat deze van het "noreply" adres van Bol komt.'
                            )}
                        </p>
                        <p>
                            <br />
                            <b>{t('tracking.info.noteLabel', 'Let op:')}</b>{' '}
                            {t(
                                'tracking.info.noteText',
                                'bij het gebruik van bepaalde kernwoorden in het onderwerp van de mail zal de e-mail ongeacht je keuze door het "noreply" adres van Bol verzonden worden. Bol geeft aan dat het om de volgende kernwoorden gaat:'
                            )}
                            <br />
                            <i>
                                {t(
                                    'tracking.info.keywords',
                                    'onderweg, verstuurd, verzonden, shipped, is geleverd, factuur, bevestiging, track & trace, track and trace, nieuwsbrief, wil je ons helpen, bedankt, thank, dankjewel, dankuwel, pakbon, wat vindt u van, levering, ingepakt, bezorgd, ingepland, aangemeld, afgeleverd, verzending, ontvangen, nota, invoice, pending, planned, received'
                                )}
                            </i>
                        </p>
                    </div>
                    <div className='footer-container'>
                        <BluemateButton onClick={onCloseInfo} label={t('tracking.close', 'Sluiten')} secondary />
                    </div>
                </div>
            </WithBackground>
        );
    }

    const isAdmin = context.userContext.user?.isSuperAdmin() ?? false;
    return (
        <>
            <WithBackground active={active} onClose={isAdmin ? onClose : voidFunction} preventBackgroundClick>
                <div className='tracking-decision default-popup'>
                    <div className='header-container'>
                        <label className='header-title'>{t('tracking.emailStats', 'E-mail statistieken')}</label>
                        {isAdmin && <CloseButton onClick={onClose} />}
                    </div>
                    <div className='body-container'>
                        <Loader active={loading} />
                        <p>
                            {t('tracking.mainIntroPrefix', 'Door een update in de mailsystemen van Bol (')}
                            <Link onClick={onOpenInfo}>{t('tracking.detailedExplanation', 'uitgebreide uitleg')}</Link>
                            {t(
                                'tracking.mainIntroSuffix',
                                ') hebben wij tijdelijk onze statistieken van de mails uit moeten schakelen. Inmiddels hebben we samen met Bol een oplossing gevonden waarmee wij de statistieken weer kunnen verzamelen:'
                            )}
                            <br />
                            <br />
                            {t(
                                'tracking.mainBody',
                                'De e-mails kunnen worden verzonden door een "noreply" adres waardoor de e-mails bij Bol door een ander mailsysteem verwerkt worden. Onze links zullen dan niet als tekst worden toegevoegd aan de e-mail. Het gevolg hiervan is dat klanten niet meer de mail zullen kunnen beantwoorden.'
                            )}
                            <br />
                            <br />
                            {t(
                                'tracking.mainQuestion',
                                'Wil je gebruik maken van de "noreply" afzender of wil je je eigen afzender gebruiken?'
                            )}
                        </p>
                    </div>
                    <div className='footer-container'>
                        <BluemateButton
                            onClick={onClose}
                            label={t('tracking.decideLater', 'Later beslissen')}
                            disabled={loading}
                            secondary
                        />
                        <div className='right-btns'>
                            <BluemateButton
                                onClick={onDecide(true)}
                                label={t('tracking.useNoreply', 'Noreply afzender gebruiken')}
                                disabled={loading}
                            />
                            <BluemateButton
                                onClick={onDecide(false)}
                                label={t('tracking.useOwn', 'Eigen afzender gebruiken')}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>
            </WithBackground>
            {renderInfo()}
        </>
    );
}
